'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';

interface SignupFormData {
  businessName: string;
  email: string;
  password: string;
  plan: string;
  agreeTerms: boolean;
}

export default function SignupForm({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { signUp } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({ defaultValues: { plan: 'starter' } });

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    setAuthError(null);
    try {
      await signUp(data.email, data.password, { fullName: data.businessName });
      const redirectTo = searchParams.get('redirectTo') || '/dashboard';
      router.push(redirectTo);
      router.refresh();
    } catch (error: any) {
      setAuthError(error?.message || 'Failed to create account. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h2 className="text-2xl font-800 text-foreground">Start building free</h2>
        <p className="text-sm text-muted-foreground mt-1">No credit card required. 14-day free trial on all plans.</p>
      </div>

      {/* Google auth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 bg-card border border-border rounded-xl text-sm font-600 text-foreground hover:bg-muted active:scale-95 transition-all duration-150 shadow-sm"
      >
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-border" />
        <span className="text-xs text-muted-foreground">or create with email</span>
        <hr className="flex-1 border-border" />
      </div>

      {/* Auth error */}
      {authError && (
        <div className="bg-danger/10 border border-danger/20 rounded-xl px-4 py-3">
          <p className="text-sm text-danger">{authError}</p>
        </div>
      )}

      {/* Business name */}
      <div>
        <label className="text-sm font-600 text-foreground block mb-1.5">Business name</label>
        <p className="text-xs text-muted-foreground mb-1.5">Used as your site name and default domain</p>
        <input
          type="text"
          placeholder="Maya's Leather Goods"
          {...register('businessName', { required: 'Business name is required', minLength: { value: 2, message: 'At least 2 characters' } })}
          className={`w-full px-4 py-3 text-sm bg-muted rounded-xl border focus:outline-none focus:bg-card transition-all ${
            errors.businessName ? 'border-danger' : 'border-transparent focus:border-primary'
          }`}
        />
        {errors.businessName && <p className="text-xs text-danger mt-1.5">{errors.businessName.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="text-sm font-600 text-foreground block mb-1.5">Email address</label>
        <input
          type="email"
          placeholder="you@yourbusiness.com"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
          className={`w-full px-4 py-3 text-sm bg-muted rounded-xl border focus:outline-none focus:bg-card transition-all ${
            errors.email ? 'border-danger' : 'border-transparent focus:border-primary'
          }`}
        />
        {errors.email && <p className="text-xs text-danger mt-1.5">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="text-sm font-600 text-foreground block mb-1.5">Password</label>
        <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters</p>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a strong password"
            {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'At least 8 characters required' } })}
            className={`w-full px-4 py-3 pr-11 text-sm bg-muted rounded-xl border focus:outline-none focus:bg-card transition-all ${
              errors.password ? 'border-danger' : 'border-transparent focus:border-primary'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>}
      </div>

      {/* Plan selection */}
      <div>
        <label className="text-sm font-600 text-foreground block mb-1.5">Choose a plan</label>
        <div className="space-y-2">
          {[
            { id: 'plan-starter', value: 'starter', name: 'Starter', price: 'Free', desc: '5 pages, 50 products, basic AI tools' },
            { id: 'plan-pro', value: 'pro', name: 'Pro', price: '$19/mo', desc: 'Unlimited pages, 1,000 products, full AI suite', popular: true },
            { id: 'plan-business', value: 'business', name: 'Business', price: '$49/mo', desc: '5,000 products, competitor radar, social selling' },
          ].map((plan) => (
            <label
              key={plan.id}
              htmlFor={plan.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/50 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-secondary/30"
            >
              <input type="radio" id={plan.id} value={plan.value} {...register('plan')} className="accent-primary" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-700 text-foreground">{plan.name}</span>
                  {plan.popular && (
                    <span className="px-1.5 py-0.5 bg-accent text-white text-[10px] font-700 rounded-full">Popular</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{plan.desc}</p>
              </div>
              <span className="text-sm font-700 text-primary font-mono-data shrink-0">{plan.price}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Terms */}
      <div>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="agreeTerms"
            {...register('agreeTerms', { required: 'You must agree to the terms' })}
            className="rounded accent-primary w-4 h-4 mt-0.5 shrink-0"
          />
          <label htmlFor="agreeTerms" className="text-xs text-muted-foreground leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-primary font-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary font-600 hover:underline">Privacy Policy</a>
          </label>
        </div>
        {errors.agreeTerms && <p className="text-xs text-danger mt-1">{errors.agreeTerms.message}</p>}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-70"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {loading ? 'Creating your account…' : 'Start Building Free'}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">
          Sign in
        </button>
      </p>
    </form>
  );
}